import { useEffect, useMemo, useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { paymentsApi, type Payment } from '@/api/paymentsApi'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Skeleton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Breadcrumbs,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { format } from 'date-fns'
import { ru, enUS } from 'date-fns/locale'
import { Link } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import '@/styles/dashboard.css'

export function AdminPaymentsPage() {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [approvingId, setApprovingId] = useState<number | null>(null)

  /**
   * Счётчик для кнопки «Фильтры»: в свёрнутой панели только статус платежа.
   * Отдельного поля поиска на странице нет — считать больше нечего.
   */
  const appliedFiltersCount = useMemo(() => (statusFilter === 'all' ? 0 : 1), [statusFilter])
  const dateLocale = i18n.language === 'ru' ? ru : enUS

  const loadPayments = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await paymentsApi.getList({ page })
      setPayments(res.data)
      setLastPage(res.lastPage)
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error?.message || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPayments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleApprove = async (payment: Payment) => {
    if (payment.status !== 'pending') return
    setApprovingId(payment.id)
    setError(null)
    try {
      await paymentsApi.approve(payment.id)
      await loadPayments()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setError(error?.response?.data?.message || error?.message || t('admin.payments.approveError'))
    } finally {
      setApprovingId(null)
    }
  }

  const displayPayments = statusFilter === 'all'
    ? payments
    : payments.filter((p) => p.status === statusFilter)

  const userName = (p: Payment) => {
    if (!p.user) return `#${p.user_id}`
    const parts = [p.user.surname, p.user.first_name].filter(Boolean)
    return parts.length ? parts.join(' ') : p.user.email
  }

  const proofUrl = (p: Payment) => {
    if (!p.proof_file) return null
    if (p.proof_file.startsWith('http')) return p.proof_file
    return `/storage/${p.proof_file}`
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.payments.title')}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.payments.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-payments" />
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('admin.payments.filtersButton')}
          />
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
              <InputLabel>{t('admin.payments.filterStatus')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('admin.payments.filterStatus')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('admin.payments.all')}</MenuItem>
                <MenuItem value="pending">{t('admin.payments.pending')}</MenuItem>
                <MenuItem value="paid">{t('admin.payments.paid')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent>
                    <Skeleton variant="text" width={60} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="80%" height={22} />
                    <Skeleton variant="text" width="60%" height={18} sx={{ mt: 0.5 }} />
                    <Skeleton variant="text" width={120} height={18} sx={{ mt: 0.5 }} />
                    <Box sx={{ mt: 1 }}>
                      <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 1 }} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('admin.payments.user')}</TableCell>
                    <TableCell>{t('admin.payments.plan')}</TableCell>
                    <TableCell>{t('admin.payments.amount')}</TableCell>
                    <TableCell>{t('admin.payments.status')}</TableCell>
                    <TableCell>{t('admin.payments.date')}</TableCell>
                    <TableCell>{t('profile.transactionsProof')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={90} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell align="right"><Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 1, ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {displayPayments.length === 0 ? (
              <Typography color="text.secondary">{t('admin.payments.noPayments')}</Typography>
            ) : (
              displayPayments.map((p) => (
                <Card key={p.id}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      #{p.id} · {p.plan?.name ?? '-'}
                    </Typography>
                    <Typography variant="body1">{userName(p)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.payments.amount')}: {p.amount} · {t('admin.payments.status')}: {p.status === 'pending' ? t('admin.payments.pending') : t('admin.payments.paid')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(p.created_at), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                    </Typography>
                    {proofUrl(p) && (
                      <Button
                        size="small"
                        variant="outlined"
                        href={proofUrl(p)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          py: 0.25,
                          px: 1,
                          minWidth: 0,
                          height: 28,
                        }}
                      >
                        {t('profile.transactionsViewProof')}
                      </Button>
                    )}
                    {p.status === 'pending' && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleApprove(p)}
                        disabled={approvingId === p.id}
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          py: 0.25,
                          px: 1,
                          minWidth: 0,
                          height: 28,
                        }}
                      >
                        {approvingId === p.id ? t('common.loading') : t('admin.payments.approve')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('admin.payments.user')}</TableCell>
                  <TableCell>{t('admin.payments.plan')}</TableCell>
                  <TableCell>{t('admin.payments.amount')}</TableCell>
                  <TableCell>{t('admin.payments.status')}</TableCell>
                  <TableCell>{t('admin.payments.date')}</TableCell>
                  <TableCell>{t('profile.transactionsProof')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t('admin.payments.noPayments')}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayPayments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.id}</TableCell>
                      <TableCell>
                        <Link to={ROUTES.ADMIN_USER_VIEW(p.user_id)} style={{ color: 'inherit' }}>
                          {userName(p)}
                        </Link>
                      </TableCell>
                      <TableCell>{p.plan?.name ?? '-'}</TableCell>
                      <TableCell>{p.amount}</TableCell>
                      <TableCell>{p.status === 'pending' ? t('admin.payments.pending') : t('admin.payments.paid')}</TableCell>
                      <TableCell>{format(new Date(p.created_at), 'dd.MM.yyyy HH:mm', { locale: dateLocale })}</TableCell>
                      <TableCell>
                        {proofUrl(p) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            href={proofUrl(p)!}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.25,
                              px: 1,
                              minWidth: 0,
                              height: 28,
                            }}
                          >
                            {t('profile.transactionsViewProof')}
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {p.status === 'pending' && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleApprove(p)}
                            disabled={approvingId === p.id}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.25,
                              px: 1,
                              minWidth: 0,
                              height: 28,
                            }}
                          >
                            {approvingId === p.id ? t('common.loading') : t('admin.payments.approve')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {lastPage > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 1 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ←
            </Button>
            <Typography sx={{ alignSelf: 'center' }}>
              {page} / {lastPage}
            </Typography>
            <Button size="small" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
              →
            </Button>
          </Box>
        )}
      </Box>
    </DashboardLayout>
  )
}
